import { newTileLinkCreator } from "../../../services/newTileLinkCreator";
import { cutIdFromMathParams } from "../../../scripts/commons/common";

export type DetailsPageType = "new" | "afterLease" | "auction";

function detailsPathSegment(pageType: DetailsPageType): string {
  switch (pageType) {
    case "new":
      return "nowe-samochody";
    case "afterLease":
      return "samochody-poleasingowe";
    case "auction":
      return "aukcje-samochodowe";
    default:
      return "nowe-samochody";
  }
}

/** Absolute URL for SPA; prefers window.origin, falls back to REACT_APP_BASE_URL. */
function toAbsoluteUrl(path: string): string {
  const pathOnly = path.startsWith("/") ? path : `/${path}`;
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}${pathOnly}`;
  }
  const envBase = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
  return envBase ? `${envBase}${pathOnly}` : pathOnly;
}

/**
 * Mirrors `Breadcrumbs/index.js` trails and appends the current vehicle detail URL.
 * JSON-LD shape follows https://schema.org/BreadcrumbList
 */
export function buildVehicleDetailsBreadcrumbListSchema(
  make: string,
  model: string,
  pageType: DetailsPageType,
  paramId: string,
  currentPageName: string
): Record<string, unknown> {
  const makeLower = make.toLowerCase().replace(/ /g, "-");
  const modelLower = model.toLowerCase().replace(/ /g, "-");
  const isNew = pageType === "new";
  const listRoot = isNew ? "/nowe-samochody" : "/samochody-poleasingowe";

  const makeLink = `${listRoot}/${makeLower}`;
  const modelLink = `${listRoot}/${makeLower}-${modelLower}`;

  const makeLabel = isNew ? `nowe samochody ${make}` : `samochody poleasingowe ${make}`;
  const modelLabel = isNew
    ? `nowe samochody ${make} - ${model}`
    : `samochody poleasingowe ${make} - ${model}`;

  const tertiaryLabel = isNew ? "nowe" : "używane";
  const tertiaryPath = isNew ? "/nowe-samochody" : "/samochody-poleasingowe";

  const id = cutIdFromMathParams(paramId);
  const currentPath = `/${detailsPathSegment(pageType)}/${newTileLinkCreator(make, model, id)}`;

  const crumbs: { position: number; name: string; path: string }[] = [
    { position: 1, name: "Strona główna", path: "/" },
    { position: 2, name: "samochody", path: "/lista-pojazdow" },
    { position: 3, name: tertiaryLabel, path: tertiaryPath },
    { position: 4, name: makeLabel, path: makeLink },
    { position: 5, name: modelLabel, path: modelLink },
    { position: 6, name: currentPageName, path: currentPath },
  ];

  const itemListElement = crumbs.map((c) => ({
    "@type": "ListItem",
    position: c.position,
    name: c.name,
    item: toAbsoluteUrl(c.path),
  }));

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement,
  };
}
